import React from 'react';
import { useQuote } from '../../contexts/QuoteContext';
import type { ModelVariant } from '../../types/quote';
import productConfig from '../../data/productConfig.json';

interface Step3ModelVariantProps {
  isActive: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
}

// Get variants from product configuration
const getAvailableVariants = (productType: string, modelId: string): ModelVariant[] => {
  const productTypeConfig = productConfig.productTypes[productType as keyof typeof productConfig.productTypes];
  if (!productTypeConfig) return [];

  const modelConfig = productTypeConfig.models[modelId as keyof typeof productTypeConfig.models];
  if (!modelConfig) return [];

  const variants = (modelConfig as any).variants;
  if (!variants) return [];

  return Object.values(variants).map((variant: any) => ({
    id: variant.id,
    name: variant.name,
    description: variant.description,
    basePrice: variant.basePrice,
    features: variant.features,
    imageUrl: 'https://placehold.co/200x200' // Placeholder image
  }));
};

const Step3ModelVariant: React.FC<Step3ModelVariantProps> = ({ isActive, isCompleted, isDisabled }) => {
  const { quoteState, updateQuoteState } = useQuote();

  const handleVariantSelect = (variant: ModelVariant) => {
    if (isDisabled) return;
    updateQuoteState({ selectedVariant: variant });
  };

  // Get available variants based on selected product and model
  const availableVariants = quoteState.selectedProduct && quoteState.selectedModel
    ? getAvailableVariants(quoteState.selectedProduct, quoteState.selectedModel.id)
    : [];

  return (
    <div className={`step-section ${isActive ? 'active' : isDisabled ? 'disabled' : 'inactive'} ${!isActive && !isCompleted ? 'collapsed' : ''}`}>
      <div className="step-header">
        <div className={`step-number ${isCompleted ? 'completed' : isActive ? 'active' : 'disabled'}`}>
          <span>3</span>
        </div>
        <h2 className="step-title">Select Model Variant</h2>
      </div>
      {(isActive || isCompleted) && (
        <div className="model-options">
          {availableVariants.map((variant) => (
            <div
              key={variant.id}
              className={`model-card ${quoteState.selectedVariant?.id === variant.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => handleVariantSelect(variant)}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            >
              <div className="model-content">
                <img
                  src={variant.imageUrl}
                  alt={variant.name}
                  className="model-image"
                />
                <h3 className="model-name">{variant.name}</h3>
                <p className="model-description">{variant.description}</p>
                <p className="model-price">From ${variant.basePrice.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Step3ModelVariant;
